<template>
    <span
        class="chip"
        :class="[variant, { overlay, 'm-right': right, 'm-left': left }]"
    >
        <Icon
            v-if="icon"
            :name="icon"
            :size="_iconSize"
            class="chip-icon"
            :style="{ color: iconColour }"
        />
        <slot v-if="html" />
        <Txt
            v-else
            v-bind="{ ...textDefault, ...text }"
            class="chip-text"
            :clamp="clamp"
            flex
        >
            <slot />
        </Txt>
    </span>
</template>

<script>

import Icon from '@/components/utility/icon';
import Txt from '@/components/form/txt';

export default {
    name: 'Chip',
    components: { Icon, Txt },
    props: {
        variant: { type: String, default: null },
        icon: { type: String, default: null, options: 'icon' },
        size: { type: String, default: null, options: 'xxxs' },
        iconSize: { type: String, default: null },
        iconColour: { type: String, default: null },
        text: { type: Object, default: () => ({}) },
        
        clamp: { type: [Boolean, Number, String], default: false },
        overlay: { type: Boolean, default: false },
        html: { type: Boolean, default: false },
        left: { type: Boolean, default: false },
        right: { type: Boolean, default: false },
    },
    slots: ['default'],
    computed: {
        textDefault() {
            if (this.overlay) return { heading: false, size: 'xs' };
            return { heading: true, size: this.size };
        },
        _iconSize() {
            if (this.iconSize) return this.iconSize;
            if (this.overlay) return '20';
            return '16';
        }
    }
};
</script>

<style lang="less" scoped>

.chip {
    --default-background-colour: var(--primary-tint);
    display: flex;
    align-items: center;
    padding: 0 8px;
    height: 24px;
    border-radius: 16px;
    color: var(--chip-colour, var(--coal));
    background-color: var(--chip-background-colour, var(--default-background-colour));
    > * { color: currentColor; }
    &.m-left  { margin-left: 8px; }
    &.m-right { margin-right: 8px; }
    &.primary { color: var(--primary); }
    &.cloud {
        background-color: var(--chip-background-colour, var(--cloud));
    }
    &.smoke {
        background-color: var(--chip-background-colour, var(--smoke));
        --chip-icon-colour: var(--coal);
    }
    &.success {
        background-color: var(--chip-background-colour, var(--success-tint));
        .chip-icon { color: var(--success); }
    }
    &.success-solid {
        background-color: var(--chip-background-colour, var(--success));
        color: var(--success-contrast);
        .chip-icon { color: var(--success-contrast); }
    }
    &.warning {
        background-color: var(--chip-background-colour, var(--warning-tint));
        .chip-icon { color: var(--warning); }
    }
    &.warning-solid {
        background-color: var(--chip-background-colour, var(--warning));
        color: var(--warning-contrast);
        .chip-icon { color: var(--warning-contrast); }
    }
    &.danger {
        background-color: var(--chip-background-colour, var(--danger-tint));
        .chip-icon { color: var(--danger); }
    }
    &.primary {
        background-color: var(--chip-background-colour, var(--primary));
        color: var(--primary-contrast);
        .chip-icon { color: var(--primary-contrast); }
    }
    &.fade-primary {
        background-color: var(--chip-background-colour, var(--primary-tint));
        color: var(--primary);
        .chip-icon { color: var(--primary); }
    }
    &.translucent {
        background-color: var(--chip-background-colour, ~"rgba(var(--coal-rgb), 0.4)");
        --chip-colour: #FFF;
        --chip-icon-colour: #FFF;
    }
    &.overlay {
        backdrop-filter: blur(8px);
        height: 32px;
        border-radius: 4px;
        padding: 0 12px;
        color: #FFF;
        background-color: var(--chip-background-colour, ~"rgba(var(--coal-rgb), 0.75)");
        > * { color: currentColor; }
        &.success {
            color: #FFF;
            background-color: var(--chip-background-colour,  var(--success));
        }
        &.warning {
            color: var(--coal);
            background-color: var(--chip-background-colour,  var(--warning));
        }
        &.danger {
            color: #FFF;
            background-color: var(--chip-background-colour,  var(--danger));
        }
        .chip-icon { color: currentColor; }
    }
    &.large {
        min-height: 32px;
        padding: 12px;
    }
}
.chip-icon {
    color: var(--chip-icon-colour, var(--primary));
    margin-right: 4px;
    margin-left: -4px;
}
</style>
